import React, { useEffect, useState } from 'react';

import {
  DayOfWeek,
  Language,
  MemberContactMethodType,
  MemberContactTimeType,
} from '@vestahealthcare/common/enums';
import Enum, { Selectable } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import {
  BaseEnum,
  CareTeamMemberType,
  Employee,
} from '@vestahealthcare/common/models';

import {
  FilterItem,
  FilterSelectable,
  Panel,
  Select,
  SelectLanguage,
  TextInput,
} from 'styleguide-v2';
import { FilterGroup } from 'styleguide-v2/src/components/Panel';

import { GetCareTeamPersonsParams } from 'dash/src/services/CareTeamServices';
import { useDebounce } from 'dash/src/utils/debounceUtils';
import { getFilterCount, getFilterItem } from 'dash/src/utils/filterUtils';

type KeyGetCareTeamParams = keyof GetCareTeamPersonsParams;

type Props = {
  data: {
    statuses: BaseEnum[];
    roles: CareTeamMemberType[];
    engagementOwners: Employee[];
    languages: Language[];
  };
  filters?: {
    [x in KeyGetCareTeamParams]?: FilterItem;
  };
  loadingFilters?: boolean;
  open: boolean;
  onChange: (
    filters: {
      [x in KeyGetCareTeamParams]?: FilterItem;
    },
  ) => void;
  onClose: () => void;
};

export const CARE_TEAM_PAID_OPTIONS: Selectable[] = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
  { label: 'Unassigned', value: 'null' },
];

const CARE_TEAM_DETAILS_FILTERS: KeyGetCareTeamParams[] = [
  'id',
  'fullName',
  'statusId',
  'typeId',
  'engagementOwnerId',
  'language',
  'contactMethods',
  'preferredDays',
  'preferredTime',
  'linkedMembers',
  'paid',
];

export const CareTeamFiltersModal = ({
  data,
  filters,
  loadingFilters,
  open,
  onChange,
  onClose,
}: Props) => {
  const [pendingFilters, setPendingFilters] = useState<
    {
      [x in KeyGetCareTeamParams]?: FilterItem;
    }
  >(filters || {});
  const [name, setName] = useState<string>();

  const debouncedName = useDebounce(name, 300);

  useEffect(() => {
    setPendingFilters(filters || {});
    setName((filters?.fullName?.value as FilterSelectable)?.value);
  }, [filters]);

  useEffect(() => {
    if (
      debouncedName !==
      (pendingFilters?.fullName?.value as FilterSelectable)?.value
    )
      setPendingFilters({
        ...pendingFilters,
        fullName: getFilterItem(
          translate('careTeam.common.name'),
          debouncedName,
        ),
      });
  }, [debouncedName]);

  return (
    <Panel.FilterModal
      open={open}
      onClear={() => {
        onChange({});
      }}
      onClose={onClose}
      onSubmit={() => onChange(pendingFilters)}
    >
      <FilterGroup
        title="Care Team Details"
        count={getFilterCount(pendingFilters, CARE_TEAM_DETAILS_FILTERS)}
        open
      >
        <Select
          allowAddNewValue
          data-cy="care-team-filter-id"
          items={[]}
          label={translate('careTeam.common.id')}
          multiple
          noOptionsText={translate('careTeam.typeToSearch')}
          onChange={(values: Selectable[]) =>
            setPendingFilters({
              ...pendingFilters,
              id: getFilterItem(
                translate('careTeam.common.id'),
                values.map(({ label }) => ({
                  value: label,
                  label,
                })),
              ),
            })
          }
          size="small"
          value={pendingFilters?.id?.value as FilterSelectable}
        />
        <TextInput
          data-cy="care-team-filter-name"
          label={translate('careTeam.common.name')}
          onChange={setName}
          size="small"
          value={name}
        />
        <Select
          data-cy="care-team-filter-status"
          items={BaseEnum.toSelectable(data.statuses)}
          label={translate('careTeam.common.status')}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(values: Selectable[]) =>
            setPendingFilters({
              ...pendingFilters,
              statusId: getFilterItem(
                translate('careTeam.common.status'),
                values,
              ),
            })
          }
          size="small"
          value={pendingFilters?.statusId?.value || []}
        />
        <Select
          data-cy="care-team-filter-role"
          items={CareTeamMemberType.toSelectable(data.roles)}
          label={translate('careTeam.common.role')}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(values: Selectable[]) =>
            setPendingFilters({
              ...pendingFilters,
              typeId: getFilterItem(translate('careTeam.common.role'), values),
            })
          }
          size="small"
          value={pendingFilters?.typeId?.value || []}
        />
        <Select
          data-cy="care-team-filters-engagementOwner"
          items={Employee.toSelectable(data.engagementOwners)}
          label={translate('careTeam.common.engagementOwner')}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(newValue: Employee[]) => {
            setPendingFilters({
              ...pendingFilters,
              engagementOwnerId: getFilterItem(
                translate('careTeam.common.engagementOwner'),
                newValue,
              ),
            });
          }}
          size="small"
          value={pendingFilters?.engagementOwnerId?.value || []}
        />
        <SelectLanguage
          data-cy="care-team-filters-language"
          items={Language.toSelectable(data.languages)}
          label={translate('careTeam.common.language')}
          loading={loadingFilters}
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              language: getFilterItem(
                translate('careTeam.common.language'),
                values,
              ),
            });
          }}
          size="small"
          value={(pendingFilters?.language?.value as Selectable[]) || []}
        />
        <Select
          data-cy="care-team-filters-contactMethods"
          items={MemberContactMethodType.toSelectable()}
          label={translate('careTeam.common.engagementPreference')}
          limitTags={1}
          multiple
          onChange={(newValue: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              contactMethods: getFilterItem(
                translate('careTeam.common.engagementPreference'),
                newValue,
              ),
            });
          }}
          size="small"
          value={pendingFilters?.contactMethods?.value || []}
        />
        <Select
          data-cy="care-team-filters-preferred-days"
          items={Enum.toSelectable(DayOfWeek.asArray)}
          label={translate('careTeam.common.preferredDays')}
          limitTags={1}
          multiple
          onChange={(newValue: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              preferredDays: getFilterItem(
                translate('careTeam.common.preferredDays'),
                newValue,
              ),
            });
          }}
          size="small"
          value={pendingFilters?.preferredDays?.value || []}
        />
        <Select
          data-cy="care-team-filters-preferred-time"
          items={Enum.toSelectable([
            MemberContactTimeType.MORNING,
            MemberContactTimeType.AFTERNOON,
          ])}
          label={translate('careTeam.common.preferredTimes')}
          limitTags={1}
          multiple
          onChange={(newValue: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              preferredTime: getFilterItem(
                translate('careTeam.common.preferredTimes'),
                newValue,
              ),
            });
          }}
          size="small"
          value={pendingFilters?.preferredTime?.value || []}
        />
        <Select.Choice
          data-cy="care-team-filters-linked-members"
          label={translate('careTeam.common.linkedMembers')}
          onChange={(value?: boolean) => {
            setPendingFilters({
              ...pendingFilters,
              linkedMembers: getFilterItem(
                translate('careTeam.common.linkedMembers'),
                value,
              ),
            });
          }}
          size="small"
          value={
            (pendingFilters?.linkedMembers?.value as FilterSelectable)
              ?.value as boolean | undefined
          }
        />
        <Select
          data-cy="care-team-filters-paid"
          items={CARE_TEAM_PAID_OPTIONS}
          label={translate('careTeam.common.paid')}
          onChange={(value?: Selectable) => {
            setPendingFilters({
              ...pendingFilters,
              paid: getFilterItem(translate('careTeam.common.paid'), value),
            });
          }}
          size="small"
          value={pendingFilters?.paid?.value as FilterSelectable}
        />
      </FilterGroup>
    </Panel.FilterModal>
  );
};
